import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IIssueApi } from "@schemas/issueSchema";
import { IconCopy } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import React, { useState } from "react";
import { usePostProjectByProjectIdIssuesCopyMutation } from "../../../../../../../openapi";
import CopyIssueModal from "../../../../../../components/CopyIssueModal/CopyIssueModal";
import ConnectedTaskDeleteCell from "../../MiniTables/ConnectedTaskDeleteCell";
import { getIssueActionNameOption } from "../../../issueUtils";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { getAgGridOtherAttributeColumns } from "./issueOtherAttributeColumns";
import { IssueTableColumnContext } from "./issueTableColumnContext";

const CONNECTED_TASK_FIELDS = [
  "id",
  "title",
  "assignedTo.name",
  "status",
  "dates.due",
  "dates.started",
  "dates.completed",
];

const createConnectedTaskColumn = (column: ColDef<IIssueApi>): ColDef<IIssueApi> => {
  const taskColumn = { ...column };
  delete taskColumn.hide;
  delete taskColumn.sort;
  delete taskColumn.sortIndex;
  delete taskColumn.type;
  return taskColumn;
};

const ConnectedTaskCopyCell = ({ issue, projectId }: { issue: IIssueApi; projectId: string }) => {
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyIssue, { isLoading }] = usePostProjectByProjectIdIssuesCopyMutation();

  const openCopyModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCopyModalOpen(true);
  };

  const submitCopy = async (args: { copyCount: number; includeUserFields: boolean }) => {
    await copyIssue({
      projectId,
      body: {
        issueId: issue._id,
        includeTasks: false,
        copyCount: args.copyCount,
        includeUserFields: args.includeUserFields,
      },
    }).unwrap();

    setCopyModalOpen(false);
    notifications.show({
      title: args.copyCount === 1 ? "Kopi opprettet" : "Kopier opprettet",
      message: args.copyCount === 1 ? "Kopien er nå opprettet." : `${args.copyCount} kopier er nå opprettet.`,
      color: "green",
    });
  };

  return (
    <>
      <CopyIssueModal
        opened={copyModalOpen}
        issueName="aksjonen"
        taskNamePlural="aksjoner"
        isLoading={isLoading}
        onClose={() => setCopyModalOpen(false)}
        onConfirm={submitCopy}
      />
      <Button aria-label="Lag kopi" size="xs" variant="subtle" onClick={openCopyModal}>
        <IconCopy size={16} />
      </Button>
    </>
  );
};

export const getConnectedTaskColumns = ({
  baseColumns,
  columnContext,
  otherAttributesTasksConfig,
}: {
  baseColumns: ColDef<IIssueApi>[];
  columnContext: IssueTableColumnContext;
  otherAttributesTasksConfig: IOtherAttributeConfigApi;
}): ColDef<IIssueApi>[] => {
  const disabledColumnsTasks = columnContext.issueBoard?.disabledColumnsTasks ?? [];
  const columnsWithCopy = baseColumns
    .filter((col) => CONNECTED_TASK_FIELDS.includes(col?.field))
    .filter((col) => !disabledColumnsTasks.includes(col.field))
    .map(createConnectedTaskColumn);

  const createCopyColumn: ColDef<IIssueApi> = {
    colId: "copy-task",
    headerName: "Lag kopi",
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    resizable: false,
    sortable: false,
    cellClass: "grid-display-block",
    cellRenderer: (params: CustomCellRendererProps<IIssueApi>) =>
      params.data == null ? null : <ConnectedTaskCopyCell issue={params.data} projectId={columnContext.projectId} />,
  };

  const createDeleteColumn: ColDef<IIssueApi> = {
    colId: "archive-task",
    headerName: "Arkiver",
    width: 72,
    minWidth: 72,
    maxWidth: 72,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    resizable: false,
    sortable: false,
    cellClass: "grid-display-block",
    cellRenderer: (params: CustomCellRendererProps<IIssueApi>) =>
      params.data == null ? null : (
        <ConnectedTaskDeleteCell
          task={params.data}
          projectId={columnContext.projectId}
          taskDefiniteLabel={
            getIssueActionNameOption(columnContext.issueBoard?.taskNameSetting, "definite") ?? "Aksjonen"
          }
          controlSize={32}
          iconSize={20}
        />
      ),
  };

  const createReorderColumn: ColDef<IIssueApi> = {
    colId: "reorder-task",
    headerName: "",
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    rowDrag: true,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    sortable: false,
    resizable: false,
    pinned: "left",
    cellClass: "ag-row-drag",
  };

  const otherAttributeTasksColumns = getAgGridOtherAttributeColumns({
    columnContext,
    isTaskColumns: true,
    otherAttributesConfig: otherAttributesTasksConfig,
  }) as ColDef<IIssueApi>[];

  return [
    createReorderColumn,
    ...columnsWithCopy,
    ...otherAttributeTasksColumns,
    createCopyColumn,
    ...(columnContext.issueBoard?.disableDeleteIssue === true ? [] : [createDeleteColumn]),
  ];
};
